"use client";

import React, { useCallback, useMemo } from "react";
import {
  useController,
  useFormContext,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { BackendMultiSelect } from "./backend-multi-select";
import type {
  ValidationPopoverBgT,
  ValidationErrorT,
} from "@/app/components/ui/validation-popover";
import type { MultiSelectOptionT } from "@/app/components/ui/multi-select";

/**
 * Props for the ControlledMultiSelect component
 *
 * Standardized interface consistent with Controlled Input Components
 */
interface ControlledMultiSelectProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  /** Field name for React Hook Form */
  name: FieldPath<TFieldValues>;
  /** React Hook Form control (optional if used within FormProvider) */
  control?: Control<TFieldValues>;
  /** Callback when field loses focus */
  onFieldBlur?: (fieldName: string) => void;
  /** Callback when selection changes */
  onSelectionChange?: (values: string[]) => void;

  // Multi-Select specific props
  /** Array of available options to choose from */
  options: MultiSelectOptionT[];
  /** Placeholder text shown when no options are selected */
  placeholder?: string;
  /** Placeholder text for the search input field */
  searchPlaceholder?: string;
  /** Message shown when no options match the search */
  emptyMessage?: string;
  /** Maximum number of selections */
  maxSelections?: number;

  // Label props (separate, since BackendMultiSelect handles label)
  /** Label displayed above the input field */
  label?: string;
  /** Whether this field is required (shows red asterisk) */
  required?: boolean;

  // Styling props
  /** Additional CSS classes for styling */
  className?: string;
  /** Whether the entire component is disabled */
  disabled?: boolean;
  /** Whether the component is read-only (makes it disabled) */
  readOnly?: boolean;

  // Validation Popover props
  /** Background variant for Validation Popover */
  bgVariant?: ValidationPopoverBgT;
  /** Close dropdown after selection */
  closeOnSelect?: boolean;
  /** Treat stored form value as single string instead of array */
  asSingleValue?: boolean;
}

/**
 * ControlledMultiSelect - Consistent with Controlled Input Components
 *
 * Features:
 * - React Hook Form integration with useController
 * - Validation Popover system for consistent error display
 * - Standardized props interface
 * - Consistent styling with Controlled Inputs
 * - Proper event handling (onBlur)
 * - TypeScript generics for type safety
 *
 * @example
 * ```tsx
 * <ControlledMultiSelect
 *   name="categories"
 *   options={CATEGORY_OPTIONS}
 *   label="Kategorien"
 *   required
 *   maxSelections={3}
 *   placeholder="Kategorien auswählen..."
 * />
 * ```
 */
export function ControlledMultiSelect<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  onFieldBlur,
  onSelectionChange,
  options,
  placeholder = "Optionen auswählen...",
  searchPlaceholder = "Suchen...",
  emptyMessage = "Keine Optionen gefunden.",
  maxSelections,
  label,
  required = false,
  className,
  disabled = false,
  readOnly,
  bgVariant = "accent",
  closeOnSelect,
  asSingleValue = false,
}: ControlledMultiSelectProps<TFieldValues>) {
  const formContext = useFormContext<TFieldValues>();
  const actualControl = control || formContext?.control;

  if (!actualControl) {
    throw new Error(
      "ControlledMultiSelect must be used within a FormProvider or with control prop"
    );
  }

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: actualControl,
  });

  // ✅ Use useWatch for reactive updates after form.reset()
  // Same configuration as ControlledSingleSelect for consistency
  const watchedValue = useWatch<TFieldValues>({
    name,
    control: actualControl,
  }) as unknown;

  const errorMessage = error?.message;
  const hasError = Boolean(errorMessage);

  // Memoize errorMessages to prevent unnecessary re-renders
  const errorMessages = useMemo(() => {
    return errorMessage ? [errorMessage] : [];
  }, [errorMessage]);

  // Event handlers
  const handleSelectionChange = useCallback(
    (values: string[]) => {
      if (asSingleValue) {
        const nextValue = values[0] ?? null;
        field.onChange(nextValue);
        onSelectionChange?.(nextValue ? [nextValue] : []);
        return;
      }
      field.onChange(values);
      onSelectionChange?.(values);
    },
    [asSingleValue, field, onSelectionChange]
  );

  const handleBlur = useCallback(() => {
    field.onBlur();
    onFieldBlur?.(name);
  }, [field, onFieldBlur, name]);

  // ✅ Use watched value for reactive updates, fallback to field.value
  // EXACT same logic as ControlledSingleSelect: watchedValue || field.value
  // This ensures consistent behavior across all controlled components
  const rawValue = watchedValue || field.value;
  const selectedValues = asSingleValue
    ? rawValue
      ? [rawValue as string]
      : []
    : Array.isArray(rawValue)
      ? (rawValue as string[])
      : [];

  const shouldShowValidation = hasError && errorMessages.length > 0;

  const wrapperClassName = className;
  const effectiveMaxSelections = asSingleValue ? 1 : maxSelections;
  const effectiveCloseOnSelect = closeOnSelect ?? false;

  return (
    <div className={wrapperClassName}>
      <BackendMultiSelect
        options={options}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        maxSelections={effectiveMaxSelections}
        label={label}
        required={required}
        className={className}
        disabled={disabled}
        readOnly={readOnly}
        onBlur={handleBlur}
        closeOnSelect={effectiveCloseOnSelect}
        selectedValues={selectedValues}
        onSelectionChange={handleSelectionChange}
        error={error?.message}
        validationMessages={
          shouldShowValidation
            ? (errorMessages as (string | ValidationErrorT)[])
            : undefined
        }
        validationBgVariant={bgVariant}
      />
    </div>
  );
}
